<script setup lang="ts">
import { ref } from 'vue';
import { STATUS_OPEN, STATUS_CLOSED } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useCruiseManagementStore } from '@/store/CruiseManagementStore';
import { useCruiseComposable } from './useCruiseComposable';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import ProgressSpinner from 'primevue/progressspinner';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import DataTable from 'primevue/datatable';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';

const { cruiseType, updateCruiseStatus, handleOpenChangeRole } = useCruiseComposable();
const cruiseManagementStore = useCruiseManagementStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const cruiseLineList = ref([]);
const cruiseLineFilters = ref();
const loading = ref(false);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);

const getCruiseLineData = () => {
  loading.value = true;
  cruiseManagementStore
    .getCruiseLineList()
    .then((response) => {
      cruiseLineList.value = response.data?.map((item) => {
        item.status = item.sts ? STATUS_OPEN : STATUS_CLOSED;
        return item;
      });
    })
    .finally(() => (loading.value = false));
};
getCruiseLineData();

const initCruiseLineFilters = () => {
  cruiseLineFilters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initCruiseLineFilters();

const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
  }
};
</script>
<template>
  <div class="card">
    <DataTable v-model:filters="cruiseLineFilters" :value="cruiseLineList" paginator showGridlines :rows="10" dataKey="id" filterDisplay="menu" :globalFilterFields="['name', 'status']">
      <template #header>
        <div class="flex justify-content-between flex-col sm:flex-row">
          <div class="flex justify-content-left flex-col sm:flex-row gap-3">
            <Button type="button" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="initCruiseLineFilters()" />
          </div>
          <div class="justify-content-right">
            <span class="p-input-icon-left mb-2">
              <i class="pi pi-search"></i>
              <InputText v-model="cruiseLineFilters['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
            </span>
          </div>
        </div>
      </template>
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <template #loading>
        <ProgressSpinner class="w-4rem h-4rem" strokeWidth="8" animationDuration="2s" />
      </template>
      <TableColumn field="name" :header="$t('management.cruise.cruise_line_name')" class="break-word" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <span class="long-text" v-tooltip="data.name">
            {{ data.name }}
          </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.cruise.search_by_cruise_line_name')" />
        </template>
      </TableColumn>
      <TableColumn field="status" :header="$t('common.status')" v-if="authorizations?.configurationPage?.airline_management_edit" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <ToggleSwitch v-model="data.status" :disabled="handleOpenChangeRole(data.status)" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="updateCruiseStatus(data.id, data.status, cruiseType.LINE)" />
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
            </template>
          </Select>
        </template>
      </TableColumn>
    </DataTable>
  </div>
</template>
